import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

interface SingleSheetDocumentConfig {
  documentName: string;
  sheetName: string;
  data: { [key: string]: any }[];
}

interface SheetColumnDataConfig {
  label: string;
  name: string;
  width?: number;
  custom?: (row: any) => string;
}

const  getExcelColumnLetter = (colNum: number) => {
  let dividend = colNum;
  let columnName = '';
  let modulo;

  while (dividend > 0) {
    modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - modulo) / 26);
  }

  return columnName;
}

const setupHeaderRow = (worksheet: ExcelJS.Worksheet, headers: string[], widths: number[]) => {
  worksheet.addRow([
    ...headers,
    '',
    'Last Updated: ' + moment().format("MM/DD/YYYY")
  ]);

  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    if (cell.value) {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF9DC3E6' }
      };
      cell.alignment = {
        wrapText: true       
      };
    }
  });  

  const columnConfig: Partial<ExcelJS.Column>[] | { width: number; }[] = [];
  widths.forEach(width => {
    columnConfig.push({ width: width });
  })

  // Updated Data.
  columnConfig.push({ width: 20 })
  columnConfig.push({ width: 25 })

  worksheet.columns = columnConfig;
  worksheet.autoFilter = {
    from: 'A1',
    to: `${getExcelColumnLetter(headers.length)}1`,
  }
}

const exportSingleSheetExcel = async (
  documentConfig: SingleSheetDocumentConfig,
  columnConfig: SheetColumnDataConfig[],
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(documentConfig.sheetName);

  const headers: string[] = [];
  const widths: number[] = [];
  columnConfig.forEach(col => {
    headers.push(col.label);
    widths.push(col.width !== undefined ? col.width : 10);


  })

  setupHeaderRow(worksheet, headers, widths);
  
  documentConfig.data.forEach(dataRow => {
    const rowValues: string[] = [];

    columnConfig.forEach(col => {
      if (col.custom) {
        rowValues.push(col.custom(dataRow) as string);
      } else {
        rowValues.push(dataRow[col.name] as string);
      }
    })

    worksheet.addRow(rowValues);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  
  saveAs(blob, `${documentConfig.documentName}.xlsx`);
}

interface MultiSheetDocumentConfig {
  documentName: string;
  sheets: {
    sheetName: string;
    data: { [key: string]: any }[];
    columns: SheetColumnDataConfig[];
  }[];
}

const exportMultiSheetExcel = async (config: MultiSheetDocumentConfig) => {
  const workbook = new ExcelJS.Workbook();

  for (const sheet of config.sheets) {
    const worksheet = workbook.addWorksheet(sheet.sheetName);
    const headers = sheet.columns.map((c) => c.label);
    const widths = sheet.columns.map((c) => c.width ?? 10);
    setupHeaderRow(worksheet, headers, widths);

    sheet.data.forEach((dataRow) => {
      const rowValues: string[] = sheet.columns.map((col) =>
        col.custom ? col.custom(dataRow) : dataRow[col.name]
      );
      const row = worksheet.addRow(rowValues);
      if (dataRow._bold) {
        row.font = { bold: true };
      }
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${config.documentName}.xlsx`);
};

export { exportSingleSheetExcel, exportMultiSheetExcel };
